import React, { useEffect, useRef } from 'react';
import PricingCard from './PricingCard';
import { pricingTiers, PlanType } from '@/config/pricing';
import { usePlan } from '@/contexts/PlanContext';
import ExperimentKitCard from './ExperimentKitCard';
import ContentMarketingSupportSection from './ContentMarketingSupportSection';
import BiomarkersSection from './BiomarkersSection';

const addOns = [{
  name: 'Peer-reviewed Science Report',
  price: 3000
}, {
  name: 'Newsletter Marketing Boost (50k subscribers)',
  price: 500
}, {
  name: 'Additional Test Boxes',
  price: 140,
  originalPrice: 175
}];

const PricingSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const {
    setSelectedPlan
  } = usePlan();

  const handlePlanSelect = (planId: PlanType) => {
    setSelectedPlan(planId);

    // Scroll to CTA section
    const ctaSection = document.getElementById('cta-section');
    if (ctaSection) {
      ctaSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const elements = entry.target.querySelectorAll('.animate-on-scroll');
          elements.forEach((el, i) => {
            setTimeout(() => {
              el.classList.add('visible');
            }, i * 100);
          });
        }
      });
    }, {
      threshold: 0.1
    });

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Define the learn more links for each plan
  const learnMoreLinks = {
    test: {
      text: "About glycemic impact tests\n",
      sectionId: "tastermonial-services",
      carouselIndex: 0
    },
    review: {
      text: "About verified user testimonials",
      sectionId: "tastermonial-services",
      carouselIndex: 1
    },
    market: {
      text: "About validated influencer video posts",
      sectionId: "tastermonial-services",
      carouselIndex: 2
    },
    report: {
      text: "About consumer evidence reports",
      sectionId: "tastermonial-services",
      carouselIndex: 3
    }
  };

  return <section ref={sectionRef} id="pricing" className="section-padding bg-white">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-16 animate-on-scroll">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Choose Your Package
            <span className="block mt-2 text-xl text-primary/80 font-medium">
              (No Subscription Required)
            </span>
          </h2>
          <p className="text-lg text-gray-600 mt-4">
            Pricing is per experiment group
          </p>
          <p className="text-sm text-gray-500 mt-2 italic">
            *Pricing covers CGM sponsorship for 10 participants and tester rewards
          </p>
        </div>

        <div className="flex flex-col md:flex-row justify-center mb-12">
          <div className="flex flex-col md:flex-row shadow-lg rounded-2xl overflow-visible w-full max-w-6xl mx-auto">
            {pricingTiers.map((tier, index) => (
              <PricingCard
                key={tier.id}
                id={tier.id}
                name={tier.name}
                price={tier.price}
                originalPrice={tier.originalPrice}
                features={tier.features}
                recommended={tier.recommended}
                credits={tier.credits}
                onSelect={tier.id === "report" ? undefined : handlePlanSelect}
                learnMoreLink={tier.id ? learnMoreLinks[tier.id] : undefined}
                position={index === 0 ? 'first' : index === pricingTiers.length - 1 ? 'last' : 'middle'}
                isContactUs={tier.isContactUs}
                contactUrl={tier.contactUrl}
                hidePrice={tier.id === "report"}
              />
            ))}
          </div>
        </div>

        {/* Add-on Sections */}
        <ContentMarketingSupportSection />
        <BiomarkersSection />

        {/* Experiment Kit Add-on Section */}
        <div className="mt-16 mb-6 pt-10">
          <div className="text-center max-w-3xl mx-auto mb-10 animate-on-scroll">
            <h3 className="text-2xl font-bold mb-2">
              Get Your Own Experiment Kit With Your Purchase
            </h3>
            <p className="text-neutral-700">Get experiment kits with your package now! Test the kit yourself or use it to build additional verified data for your product.</p>
          </div>

          <div className="max-w-3xl mx-auto">
            <ExperimentKitCard />
          </div>
        </div>
      </div>
    </section>;
};

export default PricingSection;
